import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Baseurl } from "../../config/BaseUrl";
import axios from "axios";
import { setSelectedSaloon } from "../../redux/saloon/SaloonSlice";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, FreeMode, Mousewheel } from "swiper/modules";
import "swiper/css";
import "swiper/css/free-mode";

const GenericSalon = ({
  salonType,
  title,
  useReduxSelector = false,
  fetchFromApi = true,
  apiEndpoint = null,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [salonList, setSalonList] = useState([]);
  const [loading, setLoading] = useState(true);

  const endpoint = apiEndpoint || `/api/v1/saloon/saloonfor/${salonType}`;

  // Bootstrap placeholder images
  const placeholderImages = {
    Female:
      "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' viewBox='0 0 400 300'%3E%3Crect width='400' height='300' fill='%23f8f9fa'/%3E%3Ctext x='50%25' y='50%25' font-family='Arial' font-size='18' fill='%236c757d' text-anchor='middle' dy='.3em'%3EFemale Salon%3C/text%3E%3C/svg%3E",
    Male: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' viewBox='0 0 400 300'%3E%3Crect width='400' height='300' fill='%23e9ecef'/%3E%3Ctext x='50%25' y='50%25' font-family='Arial' font-size='18' fill='%236c757d' text-anchor='middle' dy='.3em'%3EMale Salon%3C/text%3E%3C/svg%3E",
    Pet: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' viewBox='0 0 400 300'%3E%3Crect width='400' height='300' fill='%23fff3cd'/%3E%3Ctext x='50%25' y='50%25' font-family='Arial' font-size='18' fill='%236c757d' text-anchor='middle' dy='.3em'%3EPet Salon%3C/text%3E%3C/svg%3E",
    Unisex:
      "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' viewBox='0 0 400 300'%3E%3Crect width='400' height='300' fill='%23d1ecf1'/%3E%3Ctext x='50%25' y='50%25' font-family='Arial' font-size='18' fill='%236c757d' text-anchor='middle' dy='.3em'%3EUnisex Salon%3C/text%3E%3C/svg%3E",
  };

  useEffect(() => {
    const getSaloonList = async () => {
      if (fetchFromApi) {
        try {
          setLoading(true);
          const url = `${Baseurl}${endpoint}`;
          const response = await axios.get(url);
          if (response.data.success) {
            setSalonList(response.data.saloon || []);
          }
        } catch (error) {
          console.error(`Error fetching ${salonType} salons:`, error);
          // Demo data with Bootstrap styling
          setSalonList([
            {
              _id: 1,
              name: "Luxury Beauty Salon",
              rating: 4.8,
              distance: "1.2km",
              isPremium: true,
              services: "Hair • Spa • Makeup",
            },
            {
              _id: 2,
              name: "Gentlemen's Grooming",
              rating: 4.5,
              distance: "2.1km",
              isPremium: false,
              services: "Haircut • Shave • Massage",
            },
            {
              _id: 3,
              name: "Elite Hair Studio",
              rating: 4.7,
              distance: "0.8km",
              isPremium: true,
              services: "Styling • Color • Treatment",
            },
            {
              _id: 4,
              name: "Urban Beauty Parlor",
              rating: 4.3,
              distance: "1.5km",
              isPremium: false,
              services: "Nails • Facial • Waxing",
            },
            {
              _id: 5,
              name: "Premium Pet Spa",
              rating: 4.6,
              distance: "3.2km",
              isPremium: true,
              services: "Grooming • Bath • Care",
            },
          ]);
        } finally {
          setLoading(false);
        }
      }
    };
    getSaloonList();
  }, [salonType, endpoint, fetchFromApi]);

  const handleSelectedSaloon = async (salon) => {
    await dispatch(setSelectedSaloon(salon._id || salon.id));
    navigate(`/saloonNearMe`);
  };

  const getImageUrl = (salon) => {
    return (
      salon.desktopImage ||
      salon.image ||
      placeholderImages[salonType] ||
      placeholderImages.Female
    );
  };

  if (!loading && salonList.length === 0) {
    return null;
  }

  return (
    <div className="pt-4" style={{ backgroundColor: "#f5f6fb" }}>
      {/* Header Section */}
      <div className="container-fluid px-3 px-md-4 mb-4">
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex flex-column">
            <h2 className="fw-bold text-dark mb-2 fs-4">{title}</h2>
            <div
              className="bg-primary rounded"
              style={{ width: "50px", height: "3px" }}
            ></div>
          </div>
        </div>
      </div>

      {/* Salon Grid */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-muted mt-3 mb-0">Loading salons...</p>
        </div>
      ) : (
        <>
          {/* Desktop Version */}
          <div
            className="container-fluid px-0 d-none d-md-block"
            style={{ backgroundColor: "#f5f6fb" }}
          >
            <Swiper
              slidesPerView={"auto"}
              spaceBetween={16}
              freeMode={true}
              modules={[FreeMode, Mousewheel, Autoplay]}
              loop={false}
              autoplay={{
                delay: 3000,
                disableOnInteraction: false,
                pauseOnMouseEnter: true,
              }}
              breakpoints={{
                320: {
                  slidesPerView: 1.3,
                  spaceBetween: 12,
                },
                480: {
                  slidesPerView: 1.8,
                  spaceBetween: 14,
                },
                640: {
                  slidesPerView: 2.3,
                  spaceBetween: 16,
                },
                768: {
                  slidesPerView: 3,
                  spaceBetween: 16,
                },
                1024: {
                  slidesPerView: 3.5,
                  spaceBetween: 20,
                },
                1200: {
                  slidesPerView: 4.2,
                  spaceBetween: 20,
                },
              }}
              className="px-3 px-md-4"
            >
              {salonList?.map((salon, index) => (
                <SwiperSlide
                  key={`${salonType}-${salon._id || index}`}
                  style={{ width: "auto" }}
                >
                  <div
                    className="d-flex flex-column h-100 bg-white rounded-3 overflow-hidden"
                    onClick={() => handleSelectedSaloon(salon)}
                    style={{
                      cursor: "pointer",
                      transition: "transform 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-4px)";
                      e.currentTarget.style.boxShadow =
                        "0 4px 12px rgba(0,0,0,0.15)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow =
                        "0 1px 3px rgba(0,0,0,0.1)";
                    }}
                  >
                    {/* Image Container */}
                    <div
                      className="position-relative overflow-hidden"
                      style={{ height: "180px", flex: "0 0 auto" }}
                    >
                      <img
                        src={getImageUrl(salon)}
                        alt={salon.name}
                        className="w-100 h-100 object-fit-cover rounded-3"
                        onError={(e) => {
                          e.target.src =
                            placeholderImages[salonType] ||
                            placeholderImages.Female;
                        }}
                      />

                      {/* Gradient Overlay */}
                      <div
                        className="position-absolute top-0 start-0 w-100 h-100"
                        style={{
                          background:
                            "linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.1) 100%)",
                        }}
                      ></div>

                      {/* Premium Badge */}
                      {salon.isPremium && (
                        <div className="position-absolute top-0 end-0 m-2">
                          <span className="badge bg-warning text-dark fs-7 px-2 py-1 rounded-pill">
                            <i className="bi bi-star-fill me-1"></i>
                            PREMIUM
                          </span>
                        </div>
                      )}

                      {/* Rating Overlay */}
                      <div className="position-absolute bottom-0 start-0 m-2">
                        <div
                          className="d-flex align-items-center bg-dark bg-opacity-85 py-1 rounded-pill"
                          style={{ width: "3vw" }}
                        >
                          <i className="bi bi-star-fill text-warning me-1 small"></i>
                          <span className="text-white small fw-semibold w-100 h-100 d-flex justify-content-center ps-3">
                            {salon.rating || "4.5"}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Salon Info */}
                    <div className="p-3 d-flex flex-column flex-grow-1">
                      <h6 className="fw-semibold mb-1 text-truncate fs-6 text-dark">
                        {salon.name}
                      </h6>
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>

          {/* Mobile Version - Fixed 60px width */}
          <div
            className="container-fluid px-0 d-block d-md-none"
            style={{ backgroundColor: "#f5f6fb" }}
          >
            <Swiper
              slidesPerView={"auto"}
              spaceBetween={12}
              freeMode={true}
              modules={[FreeMode, Mousewheel, Autoplay]}
              loop={false}
              autoplay={{
                delay: 3000,
                disableOnInteraction: false,
                pauseOnMouseEnter: true,
              }}
              className="px-3"
            >
              {salonList.map((salon, index) => (
                <SwiperSlide
                  key={`${salonType}-mobile-${salon._id || index}`}
                  style={{ width: "80px" }} // Fixed 60px width for mobile
                >
                  <div
                    className="d-flex flex-column h-100 bg-white rounded-3 overflow-hidden"
                    onClick={() => handleSelectedSaloon(salon)}
                    style={{
                      cursor: "pointer",
                      transition: "transform 0.2s ease",
                      width: "80px", // Ensure container also has 60px width
                    }}
                  >
                    {/* Image Container - Adjusted for mobile */}
                    <div
                      className="position-relative overflow-hidden"
                      style={{ height: "60px", flex: "0 0 auto" }} // Square 60px image
                    >
                      <img
                        src={getImageUrl(salon)}
                        alt={salon.name}
                        className="w-100 h-100 object-fit-cover rounded-3"
                        onError={(e) => {
                          e.target.src =
                            placeholderImages[salonType] ||
                            placeholderImages.Female;
                        }}
                      />

                      {/* Gradient Overlay */}
                      <div
                        className="position-absolute top-0 start-0 w-100 h-100"
                        style={{
                          background:
                            "linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.1) 100%)",
                        }}
                      ></div>

                      {/* Premium Badge - Smaller for mobile */}
                      {salon.isPremium && (
                        <div className="position-absolute top-0 end-0 m-1">
                          <span className="badge bg-warning text-dark fs-8 px-1 py-0 rounded-pill">
                            <i className="bi bi-star-fill me-0"></i>
                          </span>
                        </div>
                      )}

                      {/* Rating Overlay - Smaller for mobile */}
                      <div className="position-absolute bottom-0 start-0 m-1">
                        <div
                          className="d-flex align-items-center bg-dark bg-opacity-85 py-0 rounded-pill"
                          style={{ width: "24px" }}
                        >
                          <i
                            className="bi bi-star-fill text-warning me-0"
                            style={{ fontSize: "8px" }}
                          ></i>
                          <span
                            className="text-white fw-semibold"
                            style={{ fontSize: "8px" }}
                          >
                            {salon.rating || "4.5"}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Salon Info - Adjusted for mobile */}
                    <div className="p-1 d-flex flex-column flex-grow-1 text-center">
                      <h6
                        className="fw-semibold mb-0 text-truncate"
                        style={{
                          fontSize: "10px",
                          lineHeight: "1.2",
                        }}
                      >
                        {salon.name}
                      </h6>
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </>
      )}
    </div>
  );
};

export default GenericSalon;
